import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  X,
  Upload,
  ImagePlus,
  ScanSearch,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Loader2,
} from 'lucide-react';
import { guessGenderFromName } from '../utils/guessGender';
import SelectField from './SelectField';

const STEPS = [
  { id: 'upload', label: 'Bilder' },
  { id: 'scan', label: 'Scannen' },
  { id: 'review', label: 'Prüfen' },
  { id: 'done', label: 'Fertig' },
];

const emptyContact = () => ({
  name: '',
  email: '',
  company: '',
  phone: '',
  website: '',
  notes: '',
  gender: 'unknown',
});

const ContactImageImport = ({ open, onClose, existingContacts = [], onImported }) => {
  const inputRef = useRef(null);
  const [step, setStep] = useState('upload');
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ current: 0, total: 0 });
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const stepRef = useRef(step);
  const addFilesRef = useRef(null);

  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  useEffect(() => {
    if (!open) return undefined;
    setStep('upload');
    setFiles([]);
    setPreviews([]);
    setRows([]);
    setScanning(false);
    setImporting(false);
    setImportResult(null);
    setScanProgress({ current: 0, total: 0 });
    return undefined;
  }, [open]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const selectedCount = useMemo(
    () => rows.filter((r) => r.selected && r.contact?.email).length,
    [rows]
  );

  const addFiles = (fileList, { silentEmpty = false } = {}) => {
    const incoming = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!incoming.length) {
      if (!silentEmpty) toast.error('Bitte Bilddateien auswählen (JPG, PNG, WebP…)');
      return 0;
    }
    let added = 0;
    setFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= 30) break;
        if (next.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified)) {
          continue;
        }
        next.push(file);
        added += 1;
      }
      if (next.length >= 30 && incoming.length + prev.length > 30) {
        toast.error('Maximal 30 Bilder pro Durchgang');
      }
      return next;
    });
    return incoming.length;
  };

  addFilesRef.current = addFiles;

  const filesFromClipboard = (clipboardData) => {
    if (!clipboardData) return [];
    const out = [];
    const items = clipboardData.items ? Array.from(clipboardData.items) : [];
    items.forEach((item, index) => {
      if (!item || item.kind !== 'file') return;
      if (!String(item.type || '').startsWith('image/')) return;
      const blob = item.getAsFile();
      if (!blob) return;
      const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const name = blob.name && blob.name !== 'image.png'
        ? blob.name
        : `clipboard-${Date.now()}-${index + 1}.${ext}`;
      out.push(new File([blob], name, {
        type: blob.type || 'image/png',
        lastModified: Date.now(),
      }));
    });
    if (!out.length && clipboardData.files?.length) {
      Array.from(clipboardData.files).forEach((file) => {
        if (file.type.startsWith('image/')) out.push(file);
      });
    }
    return out;
  };

  useEffect(() => {
    if (!open) return undefined;

    const onPaste = (e) => {
      if (stepRef.current !== 'upload') return;
      const images = filesFromClipboard(e.clipboardData);
      if (!images.length) return;
      e.preventDefault();
      const count = addFilesRef.current?.(images, { silentEmpty: true }) || images.length;
      if (count > 0) {
        toast.success(count === 1 ? 'Bild aus Zwischenablage eingefügt' : `${count} Bilder aus Zwischenablage eingefügt`);
      }
    };

    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [open]);

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const startScan = async () => {
    if (!files.length) {
      toast.error('Bitte mindestens ein Bild hinzufügen');
      return;
    }
    setStep('scan');
    setScanning(true);
    setScanProgress({ current: 0, total: files.length });
    const previewSnapshot = files.map((f) => URL.createObjectURL(f));

    try {
      // In Batches scannen, damit große Bulks stabil bleiben
      const batchSize = 3;
      const allResults = [];

      for (let start = 0; start < files.length; start += batchSize) {
        const batch = files.slice(start, start + batchSize);
        const formData = new FormData();
        batch.forEach((file) => formData.append('images', file));

        const res = await axios.post('/api/contacts/ocr-scan', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 180000,
        });

        const batchResults = res.data?.results || [];
        batchResults.forEach((r, idx) => {
          const imageIndex = start + idx;
          const previewUrl = previewSnapshot[imageIndex] || null;
          const fileName = r.fileName || batch[idx]?.name;
          const contacts = Array.isArray(r.contacts) && r.contacts.length
            ? r.contacts
            : (r.contact ? [r.contact] : [null]);

          contacts.forEach((rawContact, contactIdx) => {
            allResults.push({
              ...r,
              contact: rawContact,
              contactIndex: contactIdx,
              contactsFromImage: contacts.length,
              index: imageIndex,
              previewUrl,
              fileName,
            });
          });
        });
        setScanProgress({ current: Math.min(start + batch.length, files.length), total: files.length });
      }

      const known = [...existingContacts];
      const existingEmails = new Set(
        existingContacts
          .map((c) => String(c.email || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const seenInBatch = new Set();
      let droppedExisting = 0;
      let droppedInFile = 0;

      const mapped = [];
      for (const r of allResults) {
        const contact = r.contact
          ? {
              ...emptyContact(),
              ...r.contact,
              name: String(r.contact.name || '').replace(/[0-9]+/g, ' ').replace(/\s+/g, ' ').trim(),
              gender: r.contact.gender && r.contact.gender !== 'unknown'
                ? r.contact.gender
                : guessGenderFromName(r.contact.name, known),
            }
          : emptyContact();

        if (contact.name) known.push({ name: contact.name, gender: contact.gender });

        const email = String(contact.email || '').trim().toLowerCase();
        if (email && existingEmails.has(email)) {
          droppedExisting += 1;
          continue;
        }
        if (email && seenInBatch.has(email)) {
          droppedInFile += 1;
          continue;
        }
        if (email) seenInBatch.add(email);

        const multi = (r.contactsFromImage || 1) > 1;
        mapped.push({
          id: `${r.index}-${r.contactIndex || 0}-${r.fileName}`,
          index: r.index,
          contactIndex: r.contactIndex || 0,
          contactsFromImage: r.contactsFromImage || 1,
          fileName: r.fileName,
          previewUrl: r.previewUrl || previews[r.index] || null,
          success: !!r.success,
          error: r.error || '',
          selected: !!r.success && !!email,
          duplicate: false,
          contact,
          warnings: [
            ...(r.contact?.warnings || (r.error ? [r.error] : ['Keine Daten erkannt'])),
            ...(multi ? [`Kontakt ${(r.contactIndex || 0) + 1} von ${r.contactsFromImage} aus diesem Bild`] : []),
          ],
          confidence: r.contact?.confidence?.overall || 0,
          rawText: r.contact?.rawText || '',
          showRaw: false,
        });
      }

      setRows(mapped);
      setStep('review');
      const droppedParts = [];
      if (droppedExisting > 0) {
        droppedParts.push(
          droppedExisting === 1
            ? '1 bereits vorhandene E-Mail'
            : `${droppedExisting} bereits vorhandene E-Mails`
        );
      }
      if (droppedInFile > 0) {
        droppedParts.push(
          droppedInFile === 1 ? '1 Doppelte im Scan' : `${droppedInFile} Doppelte im Scan`
        );
      }
      if (droppedParts.length) {
        toast.success(`${droppedParts.join(' · ')} entfernt`);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || 'OCR-Scan fehlgeschlagen');
      setStep('upload');
    } finally {
      setScanning(false);
    }
  };

  const updateRowContact = (id, patch) => {
    setRows((prev) => {
      const next = prev.map((row) => {
        if (row.id !== id) return row;
        return { ...row, contact: { ...row.contact, ...patch } };
      });
      const existingEmails = new Set(
        existingContacts
          .map((c) => String(c.email || '').trim().toLowerCase())
          .filter(Boolean)
      );
      const seen = new Set();
      const kept = [];
      let removed = 0;
      for (const row of next) {
        const email = String(row.contact.email || '').trim().toLowerCase();
        if (email && existingEmails.has(email)) {
          removed += 1;
          continue;
        }
        if (email && seen.has(email)) {
          removed += 1;
          continue;
        }
        if (email) seen.add(email);
        kept.push({
          ...row,
          duplicate: false,
          selected: row.selected ? !!email : row.selected,
        });
      }
      if (removed > 0 && kept.length < prev.length) {
        queueMicrotask(() =>
          toast.success(
            removed === 1 ? '1 Duplikat entfernt' : `${removed} Duplikate entfernt`
          )
        );
      }
      return kept;
    });
  };

  const toggleRow = (id) => {
    setRows((prev) => prev.map((row) => {
      if (row.id !== id) return row;
      if (!row.contact.email) {
        toast.error('E-Mail wird für den Import benötigt');
        return row;
      }
      return { ...row, selected: !row.selected };
    }));
  };

  const importSelected = async () => {
    const toImport = rows
      .filter((r) => r.selected && r.contact?.email)
      .map((r) => ({
        name: r.contact.name,
        email: r.contact.email.trim().toLowerCase(),
        company: r.contact.company,
        phone: r.contact.phone,
        website: r.contact.website,
        notes: r.contact.notes,
        gender: r.contact.gender || 'unknown',
      }));

    if (!toImport.length) {
      toast.error('Keine gültigen Kontakte ausgewählt');
      return;
    }

    setImporting(true);
    try {
      const res = await axios.post('/api/contacts/bulk', { contacts: toImport });
      setImportResult(res.data);
      setStep('done');
      onImported?.(res.data);
      toast.success(`${res.data.created || 0} Kontakte importiert`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-neutral-950/60 backdrop-blur-sm">
      <div className="w-full max-w-5xl max-h-[92vh] overflow-hidden rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <ScanSearch className="w-5 h-5 text-primary-600" />
              Bild-Import (OCR)
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              Visitenkarten oder Screenshots hochladen, scannen und vor dem Import prüfen
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-neutral-100 dark:border-neutral-800 flex flex-wrap gap-2">
          {STEPS.map((s, i) => {
            const active = step === s.id;
            const done = STEPS.findIndex((x) => x.id === step) > i;
            return (
              <div
                key={s.id}
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  active
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200'
                    : done
                      ? 'border-green-300 dark:border-green-800 text-green-700 dark:text-green-300'
                      : 'border-neutral-200 dark:border-neutral-700 text-neutral-400'
                }`}
              >
                {i + 1}. {s.label}
              </div>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  addFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                className={`rounded-2xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/20'
                    : 'border-neutral-300 dark:border-neutral-700 hover:border-primary-400'
                }`}
              >
                <ImagePlus className="w-10 h-10 mx-auto text-neutral-400 mb-3" />
                <p className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                  Bilder hierher ziehen, klicken oder einfügen
                </p>
                <p className="text-xs text-neutral-500 mt-1">
                  Bulk-Upload · Clipboard (Strg+V) · max. 30 Bilder · JPG, PNG, WebP, GIF
                </p>
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {files.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {files.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="relative rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800">
                      <img src={previews[index]} alt={file.name} className="w-full h-28 object-cover" />
                      <div className="p-2 flex items-start justify-between gap-1">
                        <p className="text-[11px] text-neutral-600 dark:text-neutral-300 truncate">{file.name}</p>
                        <button type="button" onClick={() => removeFile(index)} className="text-neutral-400 hover:text-red-500">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'scan' && (
            <div className="py-16 text-center space-y-4">
              <Loader2 className="w-10 h-10 mx-auto text-primary-600 animate-spin" />
              <div>
                <p className="text-base font-medium text-neutral-900 dark:text-white">
                  {scanning ? 'Bilder werden per OCR gescannt…' : 'Scan abgeschlossen'}
                </p>
                <p className="text-sm text-neutral-500 mt-1">
                  {scanProgress.current} / {scanProgress.total} Bilder
                </p>
              </div>
              <div className="max-w-md mx-auto h-2 rounded-full bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
                <div
                  className="h-full bg-primary-600 transition-all"
                  style={{
                    width: scanProgress.total
                      ? `${Math.round((scanProgress.current / scanProgress.total) * 100)}%`
                      : '0%',
                  }}
                />
              </div>
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {rows.length} gescannt · {selectedCount} zum Import ausgewählt
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5"
                    onClick={() => setRows((prev) => prev.map((r) => ({
                      ...r,
                      selected: !!r.contact.email && !r.duplicate,
                    })))}
                  >
                    Alle gültigen wählen
                  </button>
                  <button
                    type="button"
                    className="btn-secondary text-xs !py-1.5"
                    onClick={() => setRows((prev) => prev.map((r) => ({ ...r, selected: false })))}
                  >
                    Auswahl leeren
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {rows.map((row) => (
                  <div
                    key={row.id}
                    className={`rounded-xl border p-3 sm:p-4 ${
                      row.selected
                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50/30 dark:bg-primary-950/10'
                        : 'border-neutral-200 dark:border-neutral-700'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row gap-4">
                      <div className="w-full lg:w-36 flex-shrink-0">
                        {row.previewUrl ? (
                          <img src={row.previewUrl} alt={row.fileName} className="w-full h-28 object-cover rounded-lg border border-neutral-200 dark:border-neutral-700" />
                        ) : (
                          <div className="w-full h-28 rounded-lg bg-neutral-100 dark:bg-neutral-800" />
                        )}
                        <p className="text-[11px] text-neutral-500 mt-1 truncate">{row.fileName}</p>
                        {row.contactsFromImage > 1 && (
                          <p className="text-[11px] text-primary-600 dark:text-primary-300 mt-0.5">
                            Kontakt {row.contactIndex + 1}/{row.contactsFromImage}
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-1 text-[11px]">
                          <span className={`px-1.5 py-0.5 rounded ${
                            row.confidence >= 70
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                              : row.confidence >= 40
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          }`}
                          >
                            {row.confidence}% Qualität
                          </span>
                        </div>
                      </div>

                      <div className="flex-1 space-y-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 justify-between">
                          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!row.selected}
                              onChange={() => toggleRow(row.id)}
                              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                            />
                            Für Import auswählen
                          </label>
                          {row.duplicate && (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                              <AlertTriangle className="w-3.5 h-3.5" /> Bereits vorhanden
                            </span>
                          )}
                        </div>

                        {(row.warnings || []).length > 0 && (
                          <div className="text-xs text-amber-700 dark:text-amber-300 flex flex-wrap gap-x-3 gap-y-1">
                            {row.warnings.map((w) => (
                              <span key={w} className="inline-flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> {w}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {[
                            ['name', 'Name'],
                            ['email', 'E-Mail *'],
                            ['company', 'Unternehmen'],
                            ['phone', 'Telefon'],
                            ['website', 'Website'],
                          ].map(([key, label]) => (
                            <div key={key}>
                              <label className="block text-[11px] text-neutral-500 mb-0.5">{label}</label>
                              <input
                                type="text"
                                className="input-field !py-1.5 !text-sm"
                                value={row.contact[key] || ''}
                                onChange={(e) => {
                                  const value = key === 'name'
                                    ? e.target.value.replace(/[0-9]/g, '')
                                    : e.target.value;
                                  updateRowContact(row.id, { [key]: value });
                                }}
                              />
                            </div>
                          ))}
                          <div>
                            <label className="block text-[11px] text-neutral-500 mb-0.5">Geschlecht</label>
                            <SelectField
                              className="input-field !py-1.5 !text-sm"
                              value={row.contact.gender || 'unknown'}
                              onChange={(e) => updateRowContact(row.id, { gender: e.target.value })}
                              chevronSize="w-3.5 h-3.5"
                            >
                              <option value="unknown">Unbekannt</option>
                              <option value="male">Männlich</option>
                              <option value="female">Weiblich</option>
                            </SelectField>
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-[11px] text-neutral-500 mb-0.5">Notizen</label>
                            <textarea
                              className="input-field !py-1.5 !text-sm resize-y"
                              rows={2}
                              value={row.contact.notes || ''}
                              onChange={(e) => updateRowContact(row.id, { notes: e.target.value })}
                            />
                          </div>
                        </div>

                        {row.rawText ? (
                          <button
                            type="button"
                            className="text-xs text-neutral-500 underline"
                            onClick={() => setRows((prev) => prev.map((r) => (
                              r.id === row.id ? { ...r, showRaw: !r.showRaw } : r
                            )))}
                          >
                            {row.showRaw ? 'OCR-Text ausblenden' : 'OCR-Text anzeigen'}
                          </button>
                        ) : null}
                        {row.showRaw && (
                          <pre className="text-[11px] whitespace-pre-wrap rounded-lg bg-neutral-100 dark:bg-neutral-800 p-2 text-neutral-600 dark:text-neutral-300 max-h-40 overflow-y-auto">
                            {row.rawText}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="py-12 text-center space-y-3">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Import abgeschlossen</h3>
              <p className="text-sm text-neutral-500">
                {importResult?.created || 0} Kontakte angelegt
                {importResult?.skipped ? ` · ${importResult.skipped} übersprungen` : ''}
              </p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-wrap items-center justify-between gap-2">
          <button type="button" onClick={onClose} className="btn-secondary">
            {step === 'done' ? 'Schließen' : 'Abbrechen'}
          </button>
          <div className="flex items-center gap-2">
            {step === 'review' && (
              <button type="button" className="btn-secondary" onClick={() => setStep('upload')} disabled={importing}>
                Zurück
              </button>
            )}
            {step === 'upload' && (
              <button type="button" className="btn-primary" onClick={startScan} disabled={!files.length}>
                <ScanSearch className="w-4 h-4 mr-2" />
                {files.length} Bild{files.length === 1 ? '' : 'er'} scannen
              </button>
            )}
            {step === 'review' && (
              <button type="button" className="btn-primary" onClick={importSelected} disabled={importing || selectedCount === 0}>
                {importing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importiere…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedCount} importieren
                  </>
                )}
              </button>
            )}
            {step === 'done' && (
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setStep('upload');
                  setFiles([]);
                  setRows([]);
                  setImportResult(null);
                }}
              >
                Weitere Bilder
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ContactImageImport;
